<script setup>
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { computed, onMounted, ref } from 'vue';
import { useBranding } from 'shared/composables/useBranding';
import { picoSearch } from '@scmmishra/pico-search';
import IntegrationItem from './IntegrationItem.vue';
import NewHook from './NewHook.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

const store = useStore();
const getters = useStoreGetters();
const { replaceInstallationName } = useBranding();

const searchQuery = ref('');
const uiFlags = getters['integrations/getUIFlags'];

const integrationList = computed(
  () => getters['integrations/getAppIntegrations'].value
);

const filteredIntegrationList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return integrationList.value;
  return picoSearch(integrationList.value, query, ['name', 'description']);
});

// Botón global "Crear integración" (arriba a la derecha): abre el alta de las integraciones que se crean
// desde su propia pantalla (`editable_from_list` en apps.yml). Con una sola abre directo su alta; con
// varias pregunta primero cuál.
const creatableIntegrations = computed(() =>
  integrationList.value.filter(item => item.editable_from_list)
);
const showCreatePicker = ref(false);
const showCreateModal = ref(false);
const creatingIntegrationId = ref('');

const openCreate = integrationId => {
  creatingIntegrationId.value = integrationId;
  showCreatePicker.value = false;
  showCreateModal.value = true;
};
const startCreate = () => {
  if (creatableIntegrations.value.length === 1) {
    openCreate(creatableIntegrations.value[0].id);
    return;
  }
  showCreatePicker.value = true;
};
const closeCreate = () => {
  showCreateModal.value = false;
  creatingIntegrationId.value = '';
};

onMounted(() => {
  store.dispatch('integrations/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('INTEGRATION_SETTINGS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('INTEGRATION_SETTINGS.HEADER')"
        :description="
          replaceInstallationName($t('INTEGRATION_SETTINGS.DESCRIPTION'))
        "
        :link-text="$t('INTEGRATION_SETTINGS.LEARN_MORE')"
        :search-placeholder="$t('INTEGRATION_SETTINGS.SEARCH_PLACEHOLDER')"
        feature-name="integrations"
      >
        <template v-if="creatableIntegrations.length" #actions>
          <NextButton
            data-test="create-integration"
            icon="i-lucide-plus"
            size="sm"
            :label="$t('INTEGRATION_SETTINGS.CREATE_INTEGRATION')"
            @click="startCreate"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <div class="flex-grow flex-shrink overflow-auto">
        <span
          v-if="!filteredIntegrationList.length && searchQuery"
          class="flex-1 flex items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
        >
          {{ $t('INTEGRATION_SETTINGS.NO_RESULTS') }}
        </span>
        <div
          v-else
          class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
        >
          <IntegrationItem
            v-for="item in filteredIntegrationList"
            :id="item.id"
            :key="item.id"
            :logo="item.logo"
            :name="item.name"
            :description="item.description"
            :enabled="item.enabled"
          />
        </div>
      </div>
    </template>
    <woot-modal
      v-model:show="showCreatePicker"
      :on-close="() => (showCreatePicker = false)"
    >
      <div class="flex flex-col gap-3 p-8">
        <h3 class="text-base font-medium text-n-slate-12">
          {{ $t('INTEGRATION_SETTINGS.CREATE_PICKER_TITLE') }}
        </h3>
        <NextButton
          v-for="item in creatableIntegrations"
          :key="item.id"
          faded
          slate
          :data-create-option="item.id"
          :label="item.name"
          @click="openCreate(item.id)"
        />
      </div>
    </woot-modal>
    <woot-modal v-model:show="showCreateModal" :on-close="closeCreate">
      <NewHook
        v-if="creatingIntegrationId"
        :integration-id="creatingIntegrationId"
        @close="closeCreate"
      />
    </woot-modal>
  </SettingsLayout>
</template>
